import { axiosJsonInstance } from "@/axios.instance";
import { GroupStatus } from "@/store/eventqueue/types";
import { AreaNode } from "@/types/groups/AreaNode";
import axios, { AxiosInstance, AxiosRequestConfig } from "axios";

class GroupsApi {
	private axios: AxiosInstance;

	constructor() {
		this.axios = axiosJsonInstance;
	}

	/**
	 * Loads a group tree node, and it's immediate children. Used when lazily loading the site tree.
	 * @param groupId The ID of the group to load.
	 * @param requiredPermissions The permissions required.
	 * @param multiTenant Whether the tree is being displayed in a multi-tenanted environment.
	 * @param regionUrl The URL of the region, or null.
	 * @returns The node, and any children.
	 */
	public async getGroupTreeNode(
		groupId: number,
		requiredPermissions: string[] = ["Any"],
		multiTenant: boolean = false,
		regionUrl: string = null
	): Promise<AreaNode> {
		const {
			data
		} = await this.axios.get(
			`/groups/Tree/Node/${groupId}?requiredPermissions=${requiredPermissions}&multiTenant=${multiTenant}`,
			{ regionUrl }
		);
		return data;
	}

	/**
	 * Loads the full group tree for the current user.
	 * @param requiredPermissions The permissions required.
	 * @param regionUrl The URL of the region, or null.
	 * @returns The group tree for the user.
	 */
	public async getFullGroupsTree(
		requiredPermissions: string[] = ["Any"],
		multiTenant: boolean = false,
		regionUrl: string = null
	): Promise<AreaNode[]> {
		const {
			data
		} = await this.axios.get(
			`/groups/Tree/Full?requiredPermissions=${requiredPermissions}&multiTenant=${multiTenant}`,
			{ regionUrl }
		);
		return data;
	}

	/**
	 * Loads the root group tree nodes and their immediate children.
	 * @param requiredPermissions The permissions required.
	 * @param multiTenant Whether the tree is being displayed in a multi-tenanted environment.
	 * @param regionUrl The URL of the region.
	 * @returns The root group tree nodes.
	 */
	public async getRootGroupTree(
		requiredPermissions: string[] = ["Any"],
		multiTenant: boolean = false,
		regionUrl: string = null
	): Promise<AreaNode[]> {
		const {
			data
		} = await this.axios.get(
			`/groups/Tree/Root?requiredPermissions=${requiredPermissions}&multiTenant=${multiTenant}`,
			{ regionUrl }
		);
		return data;
	}

	/**
	 * Searches the group tree for a specified query.
	 * @param query The search query.
	 * @param requiredPermissions The permissions required.
	 * @param multiTenant Whether the tree is being displayed in a multi-tenanted environment.
	 * @param regionUrl The URL of the region.
	 * @param groupsToInclude Optional, the groups to include in the result set.
	 */
	public async searchGroupTree(
		query: string,
		requiredPermissions: string[] = ["Any"],
		multiTenant: boolean = false,
		regionUrl: string = null,
		groupsToInclude: number[] = null
	): Promise<AreaNode[]> {
		const { data } = await this.axios.post(
			`groups/Tree/Search`,
			{
				query,
				requiredPermissions,
				multiTenant,
				groupsToInclude
			},
			{ regionUrl }
		);
		return data;
	}

	/**
	 * Gets a dictionary of group titles, indexed by their group id.
	 * @returns A map of group ids -> titles.
	 */
	public async getAreaDictionary(regionUrl: string = null): Promise<Map<number, string>> {
		const { data } = await this.axios.get<Map<number, string>>("/groups/Dictionary", { regionUrl });
		return data;
	}

	/**
	 * Gets a dictionary of group statuses, indexed by their group id.
	 * @returns A map of group ids -> statuses.
	 */
	public async getStatusesDictionary(regionUrl?: string): Promise<{ [groupId: number]: GroupStatus }> {
		const { data } = await this.axios.get<{ [groupId: number]: GroupStatus }>("/groups/Statuses", { regionUrl });
		return data;
	}

	public async getGroupAncestorsIdsById(groupId: number): Promise<number[]> {
		const { data } = await this.axios.get<number[]>(`/groups/Ancestors/${groupId}`);
		if (!data) {
			return [];
		}

		return data;
	}
}

const groupsApi = new GroupsApi();
export { groupsApi };
